import type { AdventureOption } from "../types/adventure";

export type ChallengeMode = "challenge" | "outcome" | "defuse" | "choose";

interface ChallengePanelProps {
  mode: ChallengeMode;
  challenge: string;
  options: [AdventureOption, AdventureOption];
  blocked: boolean;
  lastOutcome: string | null;
  lastBest?: boolean;
  onChoose: (option: AdventureOption) => void;
}

function challengeLabel(mode: ChallengeMode): string {
  switch (mode) {
    case "defuse":
      return "Je taak";
    case "choose":
      return "Beslissing";
    default:
      return "Uitdaging";
  }
}

export function ChallengeAlert({
  mode,
  challenge,
  lastOutcome,
  lastBest = false,
}: Pick<
  ChallengePanelProps,
  "mode" | "challenge" | "lastOutcome" | "lastBest"
>) {
  const alertClass =
    mode === "outcome"
      ? "act-cockpit__alert act-cockpit__alert--outcome"
      : mode === "defuse"
        ? "act-cockpit__alert act-cockpit__alert--defuse"
        : "act-cockpit__alert act-cockpit__alert--challenge";

  return (
    <div className={alertClass} role="region" aria-label="Uitdaging">
      {mode === "outcome" ? (
        <div className="act-outcome" role="status">
          <span className="act-outcome__badge">
            {lastBest ? "Goed gekozen!" : "Gelukt!"}
          </span>
          {lastOutcome && (
            <p className="act-challenge__outcome">{lastOutcome}</p>
          )}
        </div>
      ) : (
        <div className="act-challenge" key={challenge}>
          <span className="act-challenge__badge">{challengeLabel(mode)}</span>
          <p className="act-challenge__text">{challenge}</p>
        </div>
      )}
    </div>
  );
}

export function ChallengeConsole({
  mode,
  options,
  blocked,
  onChoose,
}: Pick<ChallengePanelProps, "mode" | "options" | "blocked" | "onChoose">) {
  const showButtons = mode === "challenge" || mode === "choose";
  const buttonsEnabled = showButtons && !blocked;

  if (!showButtons) return null;

  const consoleClass = [
    "act-cockpit__console",
    "act-cockpit__console--visible",
    mode === "choose" && "act-cockpit__console--solid",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={consoleClass} role="region" aria-label="Bedieningspaneel">
      <p className="act-challenge__prompt">Wat ga je doen?</p>
      <div
        className={`act-challenge__buttons${blocked ? " act-challenge__buttons--blocked" : ""}`}
      >
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={
              mode === "choose"
                ? "act-choice-btn act-choice-btn--solid"
                : "act-choice-btn"
            }
            disabled={!buttonsEnabled}
            onClick={() => onChoose(opt)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {blocked && mode === "challenge" && (
        <p className="act-challenge__blocked-hint">
          Gedachten blokkeren je knoppen…
        </p>
      )}
    </div>
  );
}

export function ChallengePanel({
  mode,
  challenge,
  options,
  blocked,
  lastOutcome,
  lastBest = false,
  onChoose,
}: ChallengePanelProps) {
  return (
    <>
      <ChallengeAlert
        mode={mode}
        challenge={challenge}
        lastOutcome={lastOutcome}
        lastBest={lastBest}
      />
      <ChallengeConsole
        mode={mode}
        options={options}
        blocked={blocked}
        onChoose={onChoose}
      />
    </>
  );
}
